import React from 'react';
import styled from 'styled-components';
import useTranslation from '@src/hooks/useTranslation';

import useSmoothScrollNavigation from '@src/hooks/useSmoothScrollNavigation';
import {
	COURSE_INTRODUCTION, PREPARATION_BEFORE_COURSE, COURSE_TRAILER, LECTURER_INTRODUCTION, DISCUSSIONS,
	PREPARATION_MATERIAL, PREPARATION_BACKGROUND, PREPARATION_OUTCOME, PREPARATION_TARGET_AUDIENCE,
} from '@src/const/course';
import {
	ContentWrapper,
	growIndicator,
} from '@src/components/common/navigationStyle';
import CoursePrice from '@src/components/course/basicInfo/courseCard/courseDetail/coursePrice';
import useCurrentCourse from '@src/hooks/useCurrentCourse';

let TAB_OPTION_LIST = [
	COURSE_INTRODUCTION,
	COURSE_TRAILER,
	PREPARATION_BEFORE_COURSE,
	LECTURER_INTRODUCTION,
	DISCUSSIONS,
];

const WidthLimitContainer = styled.div`
	width: 1200px;
	display: grid;
	grid-template-columns: 1fr 340px;

	@media ${props => props.theme.device.tablet} {
		grid-template-columns: 1fr 256px;
	}

	@media
		${props => props.theme.device.smTablet},
		${props => props.theme.device.mobile}
	{
		grid-template-columns: 1fr;
		overflow: scroll;
		-ms-overflow-style: none;  /* IE and Edge */
		scrollbar-width: none;  /* Firefox */

		&::-webkit-scrollbar {
			display: none;
		}
	}
`;

const TabsWrapper = styled.div`
	display: grid;
	grid-template-columns: repeat(${props => props.tabNumber}, 1fr);
	align-items: center;

	@media ${props => props.theme.device.mobile} {
		padding: 0px 12px;
		grid-column-gap: 16px;
	}
`;

const TabOption = styled.div`
	position: relative;
	text-align: center;
	cursor: pointer;
	color: #101010;
	opacity: ${props => (props.isActive ? 1 : 0.6)};
	white-space: nowrap;

	&:after {
		content: '';
		display: ${props => (props.isActive ? 'block' : 'none')};
		height: 4px;
		position: absolute;
		bottom: -4px;
		left: 50%;
		border-radius: 50px;
		background: #101010;
		transform: translate(-50%, 100%);
		animation: ${growIndicator} 0.2s ease-in-out forwards;
	}
`;

const PriceWrapper = styled.div`
	display: flex;
	justify-content: center;
	padding: 8px 20px;

	& > * {
		display: flex;
		justify-content: flex-end;
	}

	@media ${props => props.theme.device.desktop} {
		flex: 0 0 300px;
	}

	@media
		${props => props.theme.device.tablet},
		${props => props.theme.device.smTablet},
		${props => props.theme.device.mobile}
	{
		display: none;
	}

	.course-price__course-price-wrapper {
		display: flex;
	}

	.course-price__price {
		display: none;
	}

	.course-price__cta-button {
		width: 176px !important;
		background-color: transparent;
		color: ${props => props.theme.colors.primary};
		border: 1px solid ${props => props.theme.colors.primary} !important;
	}
`;

const discountStyle = {
	color: '#FFFFFF',
	fontSize: 20,
};

interface IContent {
	isFixed?: boolean;
}

const Content = ({ isFixed = false }: IContent): JSX.Element => {
	const { t } = useTranslation();
	const { activeTabIndex, handleClickTab } = useSmoothScrollNavigation({
		tabOptionList: TAB_OPTION_LIST,
	});
	const { currentCourse, isPreview } = useCurrentCourse();
	const { paid, bought, is_free_course } = currentCourse;

	const PREPARATION_LIST = [
		PREPARATION_MATERIAL, PREPARATION_BACKGROUND, PREPARATION_OUTCOME, PREPARATION_TARGET_AUDIENCE,
	];

	const preparationOptionExistData = PREPARATION_LIST.filter(preparationOption => !!currentCourse[preparationOption]);
	if (preparationOptionExistData.length === 0) {
		TAB_OPTION_LIST = TAB_OPTION_LIST.filter(option => option !== PREPARATION_BEFORE_COURSE);
	}
	return (
		<ContentWrapper>
			<WidthLimitContainer>
				<TabsWrapper tabNumber={5}>
					{TAB_OPTION_LIST.map((tabOptionKey, index) => (
						<TabOption
							key={tabOptionKey}
							className="content__tab"
							onClick={() => handleClickTab(tabOptionKey)}
							isActive={index === activeTabIndex}
						>
							{t(`course:information:${tabOptionKey}`)}
						</TabOption>
					))}
				</TabsWrapper>
				{isFixed && paid && (
					<PriceWrapper alignCenter={bought}>
						<CoursePrice
							showInitPrice={false}
							discountStyle={discountStyle}
							isNavbarStyle
						/>
					</PriceWrapper>
				)}
				{isFixed && !paid && is_free_course && !isPreview && (
					<PriceWrapper alignCenter={bought}>
						<CoursePrice
							showInitPrice={false}
							discountStyle={discountStyle}
							isNavbarStyle
							ctaText={t('course:free_and_go_to_course')}
						/>
					</PriceWrapper>
				)}
			</WidthLimitContainer>
		</ContentWrapper>
	);
};

export default Content;
